import { toEnglishDigits, fmtNum } from "@/utils/toEnglishDigits";
import { fmtDate, fmtTime, fmtDateTime, arabicDayOfWeek, computeDepartureRule } from "@/utils/date";
import { getBookingAirline, getBookingFlightNumber } from "@/utils/airline";
import { computeSeats } from "@/utils/seats";

export function buildWhatsAppLink(booking, phoneOverride) {
  const raw = phoneOverride || booking?.client_phone || booking?.user_phone || "";
  const phone = toEnglishDigits(String(raw)).replace(/[^0-9]/g, "");
  const lines = [
    "🌙 مراسم الزوار — تفاصيل الرحلة",
    "",
    booking?.booking_number ? `📄 رقم الحجز: ${toEnglishDigits(booking.booking_number)}` : null,
    booking?.client_name || booking?.user_name ? `👤 العميل: ${booking.client_name || booking.user_name}` : null,
    booking?.client_phone || booking?.user_phone ? `📞 الجوال: ${toEnglishDigits(booking.client_phone || booking.user_phone)}` : null,
    booking?.pickup_location ? `📍 الانطلاق: ${booking.pickup_location}` : null,
    booking?.destination || booking?.dropoff_location ? `🎯 الوجهة: ${booking.destination || booking.dropoff_location}` : null,
    booking?.pickup_time ? `🕐 الموعد: ${fmtDateTime(booking.pickup_time)}` : (booking?.trip_date ? `🕐 الموعد: ${fmtDate(booking.trip_date)} · ${fmtTime("2020-01-01T" + (booking.trip_time || "00:00"))}` : null),
    booking?.car_type_name || booking?.car_name_ar ? `🚗 السيارة: ${booking.car_type_name || booking.car_name_ar}` : null,
    booking?.passengers ? `👥 الركاب: ${fmtNum(booking.passengers)}` : null,
    booking?.total_price || booking?.price ? `💰 السعر: ${fmtNum(booking.total_price || booking.price)} ر.س` : null,
  ].filter(Boolean);
  const text = encodeURIComponent(lines.join("\n"));
  return phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
}

// Digits-only normalisation for wa.me — strips leading zeros/plus/spaces.
export function normaliseWhatsAppNumber(raw) {
  if (!raw) return "";
  return String(raw).replace(/[^\d]/g, "").replace(/^0+/, "");
}

/**
 * Rich, formal Arabic booking-confirmation message aimed at the CLIENT.
 * Supports both flat (car_name_ar/pickup/destination) and multi-car
 * `routes[].cars[]` bookings — falls back gracefully when fields missing.
 */
export function buildClientWhatsAppMessage(booking, capacityMap = {}) {
  const SEP = "━━━━━━━━━━━━━━━━━━━━";
  const routes = Array.isArray(booking?.routes) ? booking.routes : [];
  const firstRoute = routes[0] || {};
  const lastRoute = routes[routes.length - 1] || {};
  const pickup = firstRoute.pickup_location || booking?.pickup_location || "—";
  const dest = lastRoute.destination || booking?.destination || booking?.dropoff_location || "—";

  const carNames = new Set();
  for (const r of routes) {
    for (const c of (r.cars || [])) {
      const nm = c.car_name_ar || c.car_type;
      const q = Number(c.quantity || 1);
      if (nm) carNames.add(q > 1 ? `${nm} × ${toEnglishDigits(q)}` : String(nm));
    }
  }
  const carsLine = carNames.size > 0
    ? Array.from(carNames).join(" · ")
    : (booking?.car_type_name || booking?.car_name_ar || booking?.car_type || "—");

  const totalPrice = booking?.total_price != null ? booking.total_price : booking?.price;
  const total = totalPrice != null ? `${toEnglishDigits(Math.round(Number(totalPrice)))} ر.س` : "—";
  const clientName = booking?.client_name || booking?.user_name || "بكم";
  const flightNo = getBookingFlightNumber(booking);
  const airline = getBookingAirline(booking);
  const notes = String(booking?.notes || "").trim();

  const seq = booking?.seq_number || booking?.booking_number || "—";
  const tripDate = booking?.trip_date ? toEnglishDigits(booking.trip_date) : "—";
  const tripTime = booking?.trip_time ? toEnglishDigits(booking.trip_time) : "—";
  const weekday = arabicDayOfWeek(booking?.trip_date);

  const lines = [];
  lines.push("*【 مراسم الزوار 】*", "");
  lines.push(`مرحباً ${clientName}،`, "");
  lines.push("نتشرّف بخدمتكم. فيما يلي تفاصيل حجزكم:", "");
  lines.push(SEP);
  lines.push(`▪ *رقم الحجز:*  #${toEnglishDigits(String(seq))}`);
  if (weekday) lines.push(`▪ *اليوم:*      ${weekday}`);
  lines.push(`▪ *التاريخ:*    ${tripDate}`);
  lines.push(`▪ *الوقت:*      ${tripTime}`);
  // Flight number sits in the top summary block, right after time.
  if (flightNo) {
    if (airline) lines.push(`▪ *شركة الطيران:* ${airline}`);
    lines.push(`▪ *رقم الرحلة:* ${flightNo}`);
  }
  // Client-side departure time (if a rule matches or an admin override was set).
  const clientDep = computeDepartureRule(pickup, dest, booking?.trip_time, booking?.departure_time_client);
  if (clientDep) {
    const suffix = clientDep.isOverride ? "" : ` (${clientDep.label})`;
    lines.push(`▪ *وقت التحرك:* ${toEnglishDigits(clientDep.time)}${suffix}`);
  }
  lines.push(SEP, "");
  lines.push("*تفاصيل المسار*");
  lines.push(`•  من:  ${pickup}`);
  lines.push(`•  إلى: ${dest}`);
  lines.push("");
  lines.push("*تفاصيل الرحلة*");
  lines.push(`•  نوع السيارة: ${carsLine}`);
  const paxCount = Number(booking?.passengers || 0);
  if (paxCount > 0) lines.push(`•  عدد الركاب:  ${toEnglishDigits(paxCount)}`);
  lines.push("", SEP);
  lines.push("سنكون بخدمتكم في الموعد المحدد. لأي استفسار، لا تتردّدوا بالتواصل معنا.");
  lines.push("", "مع تحيات فريق *مراسم الزوار* 🌟");
  return lines.join("\n");
}

/**
 * Build a wa.me URL for the client that ships the rich booking message.
 * Use this everywhere we render a "WhatsApp client" button on a booking row.
 */
export function buildClientWhatsAppLink(booking, phoneOverride, capacityMap = {}) {
  const num = normaliseWhatsAppNumber(
    phoneOverride || booking?.client_phone || booking?.user_phone || "",
  );
  const text = encodeURIComponent(buildClientWhatsAppMessage(booking, capacityMap));
  return num ? `https://wa.me/${num}?text=${text}` : `https://wa.me/?text=${text}`;
}
